import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useApi } from '../contexts/ApiContext';
import { format, startOfMonth, endOfMonth } from 'date-fns';

const SummaryCard = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const InterchangeTab = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [subTabValue, setSubTabValue] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);

  // Default to current month
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  const [fundsTypeFilter, setFundsTypeFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      if (fundsTypeFilter) params.append('fundsType', fundsTypeFilter);

      const result = await apiCall(`/api/v1/interchange?${params.toString()}`);
      setData(result);
    } catch (err) {
      console.error('Error fetching interchange data:', err);
      setError(err.message || 'Failed to load interchange data');
    } finally {
      setLoading(false);
    }
  }, [apiCall, startDate, endDate, fundsTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const handleExportCSV = () => {
    if (!data?.adjustments?.length) return;

    const headers = [
      'Settlement Date',
      'Terminal ID',
      'Location Name',
      'Funds Type',
      'Adjustment Type',
      'Amount',
      'ABA',
      'Account',
      'Account Type',
      'Notes',
      'Source'
    ];

    const rows = data.adjustments.map(adj => [
      adj.settlementDate,
      adj.terminalId,
      adj.locationName || '',
      adj.fundsType,
      adj.adjustmentType,
      adj.amount.toFixed(4),
      adj.aba || '',
      adj.account || '',
      adj.accountType || '',
      adj.notes || '',
      adj.source || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interchange-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleUploadOpen = () => {
    setUploadOpen(true);
    setUploadData('');
    setUploadError(null);
    setUploadSuccess(null);
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    setUploadData('');
    setUploadError(null);
  };

  const parseCSVUpload = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
    const adjustments = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/['"]/g, ''));

      // Skip summary rows
      if (!values[0] || values[0].toLowerCase().includes('total')) continue;

      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });

      // Map to expected format
      const adj = {
        settlementDate: row.settlementdate || row.settlement_date || row.date || '',
        terminalId: row.terminalid || row.terminal_id || row.terminal || '',
        locationName: row.locationname || row.location_name || row.location || '',
        fundsType: row.fundstype || row.funds_type || row.type || '',
        adjustmentType: row.adjustmenttype || row.adjustment_type || row.notes || '',
        amount: parseFloat(row.amount || 0),
        aba: row.aba || '',
        account: row.account || '',
        accountType: row.accounttype || row.account_type || '',
        notes: row.notes || ''
      };

      // Validate required fields
      if (adj.settlementDate && adj.terminalId && adj.fundsType) {
        adjustments.push(adj);
      }
    }

    return adjustments;
  };

  const handleUploadSubmit = async () => {
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const adjustments = parseCSVUpload(uploadData);

      if (adjustments.length === 0) {
        throw new Error('No valid adjustment records found in CSV');
      }

      const result = await apiCall('/api/v1/interchange/upload', {
        method: 'POST',
        body: JSON.stringify({ adjustments })
      });

      setUploadSuccess(`Successfully uploaded: ${result.inserted} inserted, ${result.updated} updated, ${result.errors} errors`);

      // Refresh data after upload
      setTimeout(() => {
        fetchData();
        handleUploadClose();
      }, 2000);

    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.message || 'Failed to upload data');
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Interchange & Buy Rate Adjustments
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Upload CSV">
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={handleUploadOpen}
            >
              Upload
            </Button>
          </Tooltip>
          <Tooltip title="Export CSV">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={!data?.adjustments?.length}
            >
              Export
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Funds Type</InputLabel>
              <Select
                value={fundsTypeFilter}
                label="Funds Type"
                onChange={(e) => setFundsTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ADJC">ADJC (Interchange)</MenuItem>
                <MenuItem value="ADJD">ADJD (Buy Rate)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={fetchData}
              disabled={loading}
              fullWidth
              size="small"
            >
              {loading ? <CircularProgress size={20} /> : 'Apply'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      {data?.summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="Interchange Credits (ADJC)"
              value={formatCurrency(data.summary.totalInterchange)}
              subtitle="Total interchange credits"
              icon={<TrendingUpIcon sx={{ color: '#4caf50', fontSize: 24 }} />}
              color="#4caf50"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="Buy Rate Debits (ADJD)"
              value={formatCurrency(data.summary.totalBuyRate)}
              subtitle="Total buy rate debits"
              icon={<TrendingDownIcon sx={{ color: '#f44336', fontSize: 24 }} />}
              color="#f44336"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              title="Net Adjustment"
              value={formatCurrency(data.summary.netAdjustment)}
              subtitle={`${data.summary.recordCount || 0} records`}
              icon={<AccountBalanceIcon sx={{ color: '#2196f3', fontSize: 24 }} />}
              color={data.summary.netAdjustment >= 0 ? '#4caf50' : '#f44336'}
            />
          </Grid>
        </Grid>
      )}

      {/* Sub-Tabs */}
      <Paper>
        <Tabs value={subTabValue} onChange={handleSubTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="All Adjustments" />
          <Tab label="By Terminal" />
          <Tab label="Monthly Summary" />
        </Tabs>

        {/* All Adjustments Tab */}
        {subTabValue === 0 && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Settlement Date</TableCell>
                  <TableCell>Terminal ID</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.adjustments?.length > 0 ? (
                  data.adjustments.map((adj, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{adj.settlementDate}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {adj.terminalId}
                        </Typography>
                      </TableCell>
                      <TableCell>{adj.locationName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={adj.fundsType}
                          size="small"
                          color={adj.fundsType === 'ADJC' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{adj.adjustmentType || adj.notes || '-'}</TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="medium"
                          color={adj.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(adj.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={adj.source || 'email'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary" py={3}>
                        No adjustment records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* By Terminal Tab */}
        {subTabValue === 1 && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Terminal ID</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Interchange (ADJC)</TableCell>
                  <TableCell align="right">Buy Rate (ADJD)</TableCell>
                  <TableCell align="right">Net Adjustment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.terminalBreakdown?.length > 0 ? (
                  data.terminalBreakdown.map((term, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {term.terminalId}
                        </Typography>
                      </TableCell>
                      <TableCell>{term.locationName || '-'}</TableCell>
                      <TableCell align="right">
                        <Typography color="success.main">
                          {formatCurrency(term.interchange)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main">
                          {formatCurrency(term.buyRate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="bold"
                          color={term.netAdjustment >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(term.netAdjustment)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary" py={3}>
                        No terminal data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Monthly Summary Tab */}
        {subTabValue === 2 && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>Funds Type</TableCell>
                  <TableCell align="right">Total Amount</TableCell>
                  <TableCell align="right">Record Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.monthlyBreakdown?.length > 0 ? (
                  data.monthlyBreakdown.map((item, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        {item.month ? format(new Date(item.month), 'MMMM yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.fundsType}
                          size="small"
                          color={item.fundsType === 'ADJC' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="medium"
                          color={item.totalAmount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(item.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.recordCount}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="textSecondary" py={3}>
                        No monthly data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={handleUploadClose} maxWidth="md" fullWidth>
        <DialogTitle>Upload Adjustment Data (CSV)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste CSV data with columns: SettlementDate, TerminalID, LocationName, FundsType (ADJC/ADJD), Amount, Notes
          </Typography>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadSuccess}
            </Alert>
          )}

          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder={`SettlementDate,TerminalID,LocationName,FundsType,Amount,Notes
11/30/2025,DNS60547,Prest Test,ADJC,68.65,Interchange
11/30/2025,DNS60547,Prest Test,ADJD,-28.50,buyrate`}
            value={uploadData}
            onChange={(e) => setUploadData(e.target.value)}
            disabled={uploading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={uploading || !uploadData.trim()}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterchangeTab;
